/**
 * A prova do microfone: abre o aparelho escolhido e mostra, ao vivo, se ele
 * capta — e, quando não capta, PORQUÊ. É o "Vamos verificar" do Discord, e
 * faltava.
 *
 * Abre o aparelho POR SI (`getUserMedia` com o `deviceId` escolhido) em vez de
 * ler o traço que está a ser publicado. É mais trabalho e é de propósito: o que
 * se quer provar é que o APARELHO capta, sem a call, sem o LiveKit e sem o
 * codec pelo meio. Uma barra ligada ao traço publicado falha da mesma maneira
 * se o problema for a publicação, e não separava nada.
 *
 * A cada leitura lêem-se três bandeiras do browser que o `veredicto` usa para
 * separar causas que dão todas o mesmo zero: o `AudioContext` suspenso (o
 * medidor é que está parado), o `MediaStreamTrack.muted` (a FONTE não entrega
 * amostras — o sistema ou outra aplicação tem o aparelho) e o `readyState` (o
 * aparelho foi-se). Só quando nenhuma se aplica é que o silêncio é do microfone.
 *
 * A barra escreve-se direito no DOM, por `ref`, e não por estado do React: dez
 * leituras por segundo dentro de um painel aberto sobre uma grelha de vídeo
 * seria a grelha inteira a re-renderizar a 10Hz.
 */

import { useEffect, useRef, useState } from "react";

import { type Estado, RECADO, veredicto } from "../lib/provaDoMicrofone";

const PASSO_MS = 100;

export function ProvaDoMicrofone({ deviceId }: { deviceId: string | undefined }) {
  const barra = useRef<HTMLDivElement>(null);
  const [estado, setEstado] = useState<Estado>("a-abrir");

  useEffect(() => {
    let vivo = true;
    let stream: MediaStream | undefined;
    let contexto: AudioContext | undefined;
    let relogio: number | undefined;

    setEstado("a-abrir");

    void (async () => {
      try {
        // `ideal` e não `exact`: um aparelho que desapareceu entre listar e
        // abrir faria `exact` rebentar, e a resposta certa a isso é abrir o
        // default e dizer o que se ouve — não um erro sobre um id.
        stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { ideal: deviceId } } : true,
        });
        if (!vivo) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }

        const Contexto =
          window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Contexto) throw new Error("sem Web Audio");
        contexto = new Contexto({ latencyHint: "interactive" });
        void contexto.resume().catch(() => undefined);

        const analisador = contexto.createAnalyser();
        analisador.fftSize = 2048;
        analisador.smoothingTimeConstant = 0.4;
        analisador.minDecibels = -100;
        analisador.maxDecibels = -80;
        contexto.createMediaStreamSource(stream).connect(analisador);

        const bandas = new Uint8Array(analisador.frequencyBinCount);
        const faixa = stream.getAudioTracks()[0];
        const nascido = Date.now();
        let maximo = 0;

        relogio = window.setInterval(() => {
          const suspenso = contexto?.state === "suspended";
          // Insiste-se em acordar, em vez de esperar pelo gesto certo — mas NÃO
          // se mede enquanto está suspenso: mediria zeros e acusaria o aparelho
          // de uma coisa que é do medidor.
          if (suspenso) {
            void contexto?.resume().catch(() => undefined);
          } else {
            analisador.getByteFrequencyData(bandas);
            let soma = 0;
            for (const amplitude of bandas) soma += (amplitude / 255) ** 2;
            const nivel = Math.sqrt(soma / bandas.length);
            if (nivel > maximo) maximo = nivel;
            // A barra é generosa de propósito: a fala normal anda pela casa dos
            // 0.05, e uma barra que só enche a gritar não prova nada a ninguém.
            if (barra.current) barra.current.style.width = `${Math.min(100, nivel * 600)}%`;
          }

          setEstado(
            veredicto({
              maximo,
              desdeMs: Date.now() - nascido,
              suspenso,
              // `muted` não é o mudo do utilizador (esse é `enabled`): é o
              // browser a dizer que ninguém lhe está a entregar amostras.
              fonteMuda: faixa?.muted ?? false,
              terminado: faixa?.readyState === "ended",
            }),
          );
        }, PASSO_MS);
      } catch (error) {
        if (vivo) {
          setEstado("recusado");
          console.warn("[voz] o browser recusou abrir o microfone:", error);
        }
      }
    })();

    return () => {
      vivo = false;
      if (relogio !== undefined) window.clearInterval(relogio);
      // Largar o aparelho ao fechar não é higiene: um `getUserMedia` que fica
      // aberto mantém o LED do microfone aceso e, num aparelho que só serve um
      // consumidor de cada vez, TOMA-O a quem o quiser a seguir.
      for (const t of stream?.getTracks() ?? []) t.stop();
      void contexto?.close().catch(() => undefined);
    };
  }, [deviceId]);

  return (
    <div className={`prova prova--${estado}`}>
      <div className="prova__calha">
        <div className="prova__nivel" ref={barra} />
      </div>
      <small className="prova__recado">{RECADO[estado]}</small>
    </div>
  );
}
